import { useRef, useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, ArrowUp, ArrowDown, Plus, Loader2, Image as ImageIcon, X } from "lucide-react";

const inputCls = "w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-gold outline-none transition-colors";

export const Field = ({ label, value, onChange, placeholder, testid }) => (
  <label className="block">
    {label && <span className="block text-xs text-white/50 mb-1.5">{label}</span>}
    <input className={inputCls} value={value ?? ""} placeholder={placeholder} data-testid={testid} onChange={(e) => onChange(e.target.value)} />
  </label>
);

export const TextArea = ({ label, value, onChange, rows = 3, placeholder, testid }) => (
  <label className="block">
    {label && <span className="block text-xs text-white/50 mb-1.5">{label}</span>}
    <textarea className={`${inputCls} resize-y`} rows={rows} value={value ?? ""} placeholder={placeholder} data-testid={testid} onChange={(e) => onChange(e.target.value)} />
  </label>
);

export const ImageField = ({ label, value, onChange, testid }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [libOpen, setLibOpen] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(res.data.url);
      toast.success("画像をアップロードしました");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <div>
      {label && <span className="block text-xs text-white/50 mb-1.5">{label}</span>}
      <div className="flex items-center gap-3">
        {value ? (
          <img src={value} alt="" className="h-14 w-14 rounded-lg object-cover border border-white/15 bg-black/30" />
        ) : (
          <div className="h-14 w-14 rounded-lg border border-dashed border-white/20 grid place-items-center text-white/30 text-[10px]">なし</div>
        )}
        <input className={inputCls} value={value ?? ""} placeholder="画像URL / アップロード / ライブラリ" data-testid={testid} onChange={(e) => onChange(e.target.value)} />
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-gold/40 text-gold px-3 py-2 text-xs hover:bg-gold hover:text-ink transition-colors disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} 画像
        </button>
        <button type="button" onClick={() => setLibOpen(true)} className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-white/20 text-white/70 px-3 py-2 text-xs hover:border-gold/50 hover:text-gold transition-colors" data-testid={testid ? `${testid}-lib` : undefined}>
          <ImageIcon size={14} /> ライブラリ
        </button>
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} />
      </div>
      {libOpen && <MediaLibrary onClose={() => setLibOpen(false)} onPick={(u) => { onChange(u); setLibOpen(false); }} />}
    </div>
  );
};

export const MediaLibrary = ({ onClose, onPick }) => {
  const [items, setItems] = useState(null);
  const load = () => api.get("/media").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const del = async (id, e) => {
    e.stopPropagation();
    try { await api.delete(`/media/${id}`); setItems((x) => x.filter((m) => m.id !== id)); } catch { toast.error("削除に失敗"); }
  };
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 backdrop-blur-md p-6" onClick={onClose} data-testid="media-library">
      <div className="w-full max-w-3xl max-h-[85vh] overflow-y-auto rounded-2xl border border-gold/30 bg-surface p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-jp text-lg font-bold text-white">画像ライブラリ</h3>
          <button onClick={onClose} className="text-white/50 hover:text-gold"><X size={20} /></button>
        </div>
        {items === null ? (
          <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-gold" /></div>
        ) : items.length === 0 ? (
          <p className="py-16 text-center text-white/40 text-sm">アップロード済みの画像はありません。</p>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {items.map((m) => (
              <button key={m.id} type="button" onClick={() => onPick(m.url)} className="group relative aspect-square rounded-lg overflow-hidden border border-white/10 hover:border-gold transition-colors" data-testid={`media-${m.id}`}>
                <img src={m.url} alt="" className="h-full w-full object-cover" />
                <span onClick={(e) => del(m.id, e)} className="absolute top-1 right-1 grid place-items-center h-6 w-6 rounded bg-black/60 text-white/70 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={13} /></span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Generic list editor with add / delete / move up-down
export const ListEditor = ({ items, setItems, renderItem, makeNew, addLabel = "項目を追加", testid }) => {
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
  };
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));
  const update = (i, patch) => setItems(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));

  return (
    <div className="space-y-4" data-testid={testid}>
      {items.map((it, i) => (
        <div key={it.id || i} className="rounded-xl border border-white/10 bg-black/20 p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-mono text-white/40">#{i + 1}</span>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => move(i, -1)} className="p-1.5 rounded text-white/50 hover:text-gold disabled:opacity-30" disabled={i === 0}><ArrowUp size={15} /></button>
              <button type="button" onClick={() => move(i, 1)} className="p-1.5 rounded text-white/50 hover:text-gold disabled:opacity-30" disabled={i === items.length - 1}><ArrowDown size={15} /></button>
              <button type="button" onClick={() => remove(i)} className="p-1.5 rounded text-white/50 hover:text-red-400"><Trash2 size={15} /></button>
            </div>
          </div>
          {renderItem(it, (patch) => update(i, patch))}
        </div>
      ))}
      <button type="button" onClick={() => setItems([...items, makeNew()])} className="inline-flex items-center gap-2 rounded-lg border border-white/15 px-4 py-2 text-sm text-white/70 hover:text-gold hover:border-gold/40 transition-colors">
        <Plus size={16} /> {addLabel}
      </button>
    </div>
  );
};

export const StringListEditor = ({ items, setItems, placeholder = "テキスト", addLabel = "追加" }) => {
  const arr = items || [];
  return (
    <ListEditor
      items={arr.map((v, i) => (typeof v === "string" ? { id: `str${i}`, _v: v } : v))}
      setItems={(next) => setItems(next.map((x) => x._v ?? ""))}
      makeNew={() => ({ id: `str${Date.now()}`, _v: "" })}
      addLabel={addLabel}
      renderItem={(it, on) => <Field value={it._v} placeholder={placeholder} onChange={(v) => on({ _v: v })} />}
    />
  );
};
